import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import {
	type ActionFunctionArgs,
	data,
	redirect,
	useFetcher,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/errors/error-boundary.tsx'
import { ErrorList, PublicField } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { BASE_URL } from '#app/utils/config.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { type ISupportedLanguage } from '#app/utils/i18n.tsx'
import {
	generateMetaTranslationLoader,
	i18next,
} from '#app/utils/i18next.server.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import routes from '#routes/index.tsx'
import { type Route } from '../../../../.react-router/types/app/routes/($lang)+/_auth+/+types/forgot-password.ts'
import { PublicForgotPasswordEmail } from '../../../../emails/public/authentication/forgot-password.tsx'
import { emailTranslations } from '../../../../emails/translations.tsx'
import { prepareVerification } from './verify.server.ts'

const ForgotPasswordSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export async function loader({ request }: Route.LoaderArgs) {
	return {
		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'auth',
			keys: {
				title: 'forgotPassword.title',
				description: 'forgotPassword.description',
				canonical: `${BASE_URL}${routes.public.auth.forgotPassword.route({})}`,
			},
		}),
	}
}

export const meta = generateTranslatedMeta

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const locale = (await i18next.getLocale(request)) as ISupportedLanguage
	const t = await i18next.getFixedT(request, 'auth')

	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const user = await prisma.user.findFirst({
				where: {
					OR: [
						{ email: data.usernameOrEmail },
						{ username: data.usernameOrEmail },
					],
				},
				select: { id: true },
			})
			if (!user) {
				ctx.addIssue({
					path: ['usernameOrEmail'],
					code: z.ZodIssueCode.custom,
					message: t('validation.reviewEmailOrUsername'),
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { usernameOrEmail } = submission.value

	const user = await prisma.user.findFirstOrThrow({
		where: { OR: [{ email: usernameOrEmail }, { username: usernameOrEmail }] },
		select: { email: true, username: true },
	})

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'reset-password',
		target: usernameOrEmail,
	})

	const response = await sendEmail({
		to: user.email,
		subject: `${emailTranslations[locale].authentication.forgotPassword.title}`,
		react: (
			<PublicForgotPasswordEmail
				locale={locale}
				onboardingUrl={verifyUrl.toString()}
				otp={otp}
			/>
		),
	})

	if (response.status === 'success') {
		throw redirect(redirectTo.toString())
	} else {
		return data(
			{ result: submission.reply({ formErrors: [response.error.message] }) },
			{ status: 500 },
		)
	}
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()
	const { t } = useTranslation('auth')

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container flex min-h-full flex-col justify-center">
			<div className="flex flex-col items-center justify-center gap-sm">
				<Text
					className={'w-fit text-center'}
					as={'h1'}
					size={'h2'}
					type={'serif'}
					weight={'light'}
				>
					{t('forgotPassword.title')}
				</Text>
				<Text>{t('forgotPassword.subtitle')}</Text>
				<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
					<forgotPassword.Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<div>
							<PublicField
								placeholder={t('fields.usernameOrEmail')}
								inputProps={{
									autoFocus: true,
									...getInputProps(fields.usernameOrEmail, { type: 'text' }),
								}}
								errors={fields.usernameOrEmail.errors}
							/>
						</div>
						<ErrorList errors={form.errors} id={form.errorId} />

						<div className="mt-6">
							<StatusButton
								variant={'outline'}
								className="w-full"
								status={
									forgotPassword.state === 'submitting'
										? 'pending'
										: (form.status ?? 'idle')
								}
								type="submit"
								disabled={forgotPassword.state !== 'idle'}
							>
								{t('actions.recoverPassword')}
							</StatusButton>
						</div>
					</forgotPassword.Form>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
